import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Mail, MessageSquare, FileText, Receipt, Plus, Phone, MapPin, Send, Download, Bell, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Empty = ({ msg }) => <div className="p-8 text-center text-sm text-slate-500">{msg}</div>;

export default function CustomerDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = async () => setData((await api.get(`/customers/${cid}/history`)).data);
  useEffect(() => { load(); }, [cid]);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { customer, quotes, invoices, communications, totals } = data;

  const markPaid = async (inv) => {
    await api.patch(`/invoices/${inv.id}/status`, { status: "paid" });
    toast.success("Marked paid"); load();
  };
  const remind = async (inv) => {
    try { await api.post("/reminders/send", { entity_type: "invoice", entity_id: inv.id, channel: "email" }); toast.success("Reminder sent"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="customer-detail">
      <button onClick={() => nav("/dashboard/customers")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to customers
      </button>

      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{customer.name}</h1>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
            {customer.email && <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" /> {customer.email}</div>}
            {customer.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" /> {customer.phone}</div>}
            {customer.address && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" /> {customer.address}</div>}
          </div>
          {customer.notes && <p className="mt-2 text-sm text-slate-500 max-w-2xl">{customer.notes}</p>}
        </div>
        <Button data-testid="new-quote-for-customer" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => nav(`/dashboard/quotes/new?customer=${cid}`)}>
          <Plus className="w-4 h-4 mr-1.5" /> New Quote
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="customer-kpis">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Revenue</div>
          <div className="font-display text-2xl font-extrabold text-emerald-600 mt-1">{fmt(totals.revenue)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Outstanding</div>
          <div className="font-display text-2xl font-extrabold text-amber-600 mt-1">{fmt(totals.outstanding)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Quotes</div>
          <div className="font-display text-2xl font-extrabold mt-1">{totals.quotes_count}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Invoices</div>
          <div className="font-display text-2xl font-extrabold mt-1">{totals.invoices_count}</div>
        </div>
      </div>

      <Tabs defaultValue="quotes" className="mt-6">
        <TabsList data-testid="history-tabs">
          <TabsTrigger value="quotes" data-testid="tab-quotes"><FileText className="w-4 h-4 mr-1.5" /> Quotes ({quotes.length})</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt className="w-4 h-4 mr-1.5" /> Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages"><Mail className="w-4 h-4 mr-1.5" /> Messages ({communications.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="quotes">
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            {quotes.length === 0 ? <Empty msg="No quotes for this customer yet." /> : (
              <table className="w-full text-sm" data-testid="quotes-history-table">
                <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3">Number</th>
                    <th className="text-left px-5 py-3">Title</th>
                    <th className="text-right px-5 py-3">Total</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3">Created</th>
                    <th className="text-right px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} className="odd:bg-slate-50/50 border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono font-semibold">
                        <Link to={`/dashboard/quotes/${q.id}`} className="hover:text-blue-600">{q.number}</Link>
                      </td>
                      <td className="px-5 py-3 text-slate-600 max-w-[240px] truncate">{q.title}</td>
                      <td className="px-5 py-3 text-right font-medium">{fmt(q.total)}</td>
                      <td className="px-5 py-3"><span className={`status-pill status-${q.status}`}>{q.status}</span></td>
                      <td className="px-5 py-3 text-slate-600">{new Date(q.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button title="PDF" onClick={() => window.open(`${API}/quotes/${q.id}/pdf`, "_blank")} className="p-1.5 hover:bg-slate-100 rounded"><Download className="w-4 h-4 text-slate-600" /></button>
                        <button title="Open public link" onClick={() => window.open(`/quote/${q.id}`, "_blank")} className="p-1.5 hover:bg-slate-100 rounded"><Send className="w-4 h-4 text-blue-600" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="invoices">
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
            {invoices.length === 0 ? <Empty msg="No invoices for this customer yet." /> : (
              <table className="w-full text-sm" data-testid="invoices-history-table">
                <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-5 py-3">Number</th>
                    <th className="text-left px-5 py-3">Title</th>
                    <th className="text-right px-5 py-3">Total</th>
                    <th className="text-left px-5 py-3">Status</th>
                    <th className="text-left px-5 py-3">Due</th>
                    <th className="text-right px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="odd:bg-slate-50/50 border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-5 py-3 font-mono font-semibold">
                        <Link to={`/dashboard/invoices/${inv.id}`} className="hover:text-blue-600">{inv.number}</Link>
                      </td>
                      <td className="px-5 py-3 text-slate-600 max-w-[240px] truncate">{inv.title}</td>
                      <td className="px-5 py-3 text-right font-medium">{fmt(inv.total)}</td>
                      <td className="px-5 py-3"><span className={`status-pill status-${inv.status}`}>{inv.status}</span></td>
                      <td className="px-5 py-3 text-slate-600">{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : "—"}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button title="PDF" onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")} className="p-1.5 hover:bg-slate-100 rounded"><Download className="w-4 h-4 text-slate-600" /></button>
                        {inv.status !== "paid" && <>
                          <button title="Reminder" onClick={() => remind(inv)} className="p-1.5 hover:bg-slate-100 rounded"><Bell className="w-4 h-4 text-amber-600" /></button>
                          <button title="Mark paid" onClick={() => markPaid(inv)} className="p-1.5 hover:bg-slate-100 rounded"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></button>
                        </>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="messages">
          <div className="bg-white border border-slate-200 rounded-md">
            {communications.length === 0 ? <Empty msg="No messages sent to this customer yet." /> : (
              <ul className="divide-y divide-slate-100" data-testid="messages-history-list">
                {communications.map((m) => (
                  <li key={m.id} className="p-5 flex items-start gap-4">
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${m.channel === "email" ? "bg-blue-50 text-blue-600 border border-blue-100" : "bg-emerald-50 text-emerald-600 border border-emerald-100"}`}>
                      {m.channel === "email" ? <Mail className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="font-bold uppercase tracking-wider">{m.channel}</span>
                        <span>·</span>
                        <span>{new Date(m.created_at).toLocaleString()}</span>
                        <span>·</span>
                        <span className="status-pill status-accepted">{m.status}</span>
                        {m.entity_type && <>
                          <span>·</span>
                          <span className="capitalize">{m.entity_type}</span>
                        </>}
                      </div>
                      <div className="mt-1 text-sm">
                        <span className="font-semibold text-slate-900">To:</span> <span className="text-slate-700">{m.to}</span>
                        {m.subject && <> · <span className="font-semibold text-slate-900">Subj:</span> <span className="text-slate-700">{m.subject}</span></>}
                      </div>
                      <div className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{m.message}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
